import { clTransform } from 'app/utils/cloudinaryUtils'
import { defaultIfNoValue } from 'app/utils/styleUtils'
import React from 'react'
import styled, { css } from 'styled-components'

export type BgShapesVariant = 'bottom-section-wave'

type Props = {
  bgImage: string
  bgImageMobile?: string
  bgPosition?: string
  id?: string
  marginTop?: number
  marginTopMobile?: number
  marginBottom?: number
  marginBottomMobile?: number
  paddingTop?: number
  paddingTopMobile?: number
  paddingBottom?: number
  paddingBottomMobile?: number
  className?: string
}

export const BgRetina = ({
  id,
  bgImage,
  bgImageMobile,
  bgPosition,
  marginTop,
  marginTopMobile,
  marginBottom,
  marginBottomMobile,
  paddingTop,
  paddingTopMobile,
  paddingBottom,
  paddingBottomMobile,
  className,
  children,
}: WithChildren<Props>): JSX.Element => {
  return bgImage && bgImage.includes('.svg') ? (
    <WrapperSimple
      id={id}
      className={className}
      marginTop={marginTop}
      marginTopMobile={marginTopMobile}
      marginBottom={marginBottom}
      marginBottomMobile={marginBottomMobile}
      bgImage={bgImage}
      bgImageMobile={bgImageMobile}
      bgPosition={bgPosition}
      paddingTop={paddingTop}
      paddingTopMobile={paddingTopMobile}
      paddingBottom={paddingBottom}
      paddingBottomMobile={paddingBottomMobile}
    >
      {children}
    </WrapperSimple>
  ) : (
    <WrapperRetina
      id={id}
      className={className}
      marginTop={marginTop}
      marginTopMobile={marginTopMobile}
      marginBottom={marginBottom}
      marginBottomMobile={marginBottomMobile}
      paddingTop={paddingTop}
      paddingTopMobile={paddingTopMobile}
      paddingBottom={paddingBottom}
      paddingBottomMobile={paddingBottomMobile}
      bgPosition={bgPosition}
      bgImageMobile={
        bgImageMobile &&
        clTransform({
          url: bgImageMobile,
        })
      }
      bgImageMobile1x={
        bgImageMobile &&
        clTransform({
          url: bgImageMobile,
          half: true,
        })
      }
      backgroundImage={
        bgImage &&
        clTransform({
          url: bgImage,
        })
      }
      backgroundImage1x={
        bgImage &&
        clTransform({
          url: bgImage,
          half: true,
        })
      }
    >
      {children}
    </WrapperRetina>
  )
}

type SpacingProps = Pick<
  Props,
  | 'paddingBottom'
  | 'paddingBottomMobile'
  | 'paddingTop'
  | 'paddingTopMobile'
  | 'marginBottom'
  | 'marginBottomMobile'
  | 'marginTop'
  | 'marginTopMobile'
>

const handlePadding = ({
  paddingBottom,
  paddingBottomMobile,
  paddingTop,
  paddingTopMobile,
  marginBottom,
  marginBottomMobile,
  marginTop,
  marginTopMobile,
}: SpacingProps) => css`
  padding-top: ${defaultIfNoValue(3, paddingTopMobile || paddingTop)}rem;
  padding-bottom: ${defaultIfNoValue(
    3,
    paddingBottomMobile || paddingBottom
  )}rem;

  margin-top: ${defaultIfNoValue(0, marginTopMobile || marginTop)}rem;
  margin-bottom: ${defaultIfNoValue(0, marginBottomMobile || marginBottom)}rem;

  @media (min-width: ${({ theme }) => theme.bp.tablet}) {
    padding-top: ${defaultIfNoValue(6, paddingTop)}rem;
    padding-bottom: ${defaultIfNoValue(6, paddingBottom)}rem;

    margin-top: ${defaultIfNoValue(0, marginTop)}rem;
    margin-bottom: ${defaultIfNoValue(0, marginBottom)}rem;
  }
`

type WrapperSimpleProps = Pick<
  Props,
  'bgImageMobile' | 'bgImage' | 'bgPosition'
> &
  SpacingProps

const WrapperSimple = styled.section<WrapperSimpleProps>`
  background-position: ${({ bgPosition }) => bgPosition || 'top center'};
  background-repeat: no-repeat;

  background-image: url('${({ bgImageMobile }) => bgImageMobile}');
  @media (min-width: ${({ theme }) => theme.bp.tablet}) {
    background-image: url('${({ bgImage }) => bgImage}');
  }

  ${handlePadding};
`

type WrapperRetinaProps = {
  backgroundImage: string
  backgroundImage1x: string
  bgImageMobile?: string
  bgImageMobile1x?: string
  bgPosition?: string
} & SpacingProps

const WrapperRetina = styled.section<WrapperRetinaProps>`
  background-repeat: no-repeat;
  background-position: ${({ bgPosition }) => bgPosition || 'top center'};

  ${({ bgImageMobile, bgImageMobile1x }) =>
    bgImageMobile
      ? css`
          background-image: url('${bgImageMobile1x}');
          background-image: image-set(url('${bgImageMobile}') 2x);
        `
      : null};
  ${({ backgroundImage, backgroundImage1x, theme }) =>
    backgroundImage
      ? css`
          @media (min-width: ${theme.bp.tablet}) {
            background-image: url('${backgroundImage1x}');
            background-image: image-set(url('${backgroundImage}') 2x);
          }
        `
      : null};

  ${handlePadding};
`
